import { useCallback, useEffect, useRef, useState } from 'react';
import Constants from '../../../app/utils/constants';
import TestimonialCard from '../../atoms/TestimonialCard/TestimonialCard';
import TitleHeader from '../../atoms/TitleHeader/TitleHeader';
import colors from '../../theme/color';
import type { SectionTheme } from '../../theme/sectionTheme';

type TestimonialThemeColor = 'purple' | 'green' | 'rabbitx' | 'arbventure' | 'tatvacaretheme' | 'ddxtheme' | 'medilearntheme';

type TestimonialItem = {
  logo: string;
  description: string;
  star?: string;
  name: string;
  roleAtCompany?: string;
  program?: string;
  ctc?: string;
  emoji?: string;
};

type TestimonialProps = {
  showHeader?: boolean;
  className?: string;
  titleBadgeClassName?: string;
  titleBadgeBorderClassName?: string;
  themeColor?: TestimonialThemeColor;
  indicatorActiveColor?: string;
  indicatorBorderColor?: string;
  data?: TestimonialItem[];
};

const VISIBLE_COUNT = 3;
const ROTATE_INTERVAL_MS = 2500;
const SLIDE_DURATION_MS = 2500;
const SLIDE_PREP_MS = 10;

const Testimonial = ({
  showHeader = true,
  className = '',
  titleBadgeClassName = '',
  titleBadgeBorderClassName = '',
  themeColor = 'purple',
  indicatorActiveColor,
  indicatorBorderColor,
  data,
}: TestimonialProps) => {
  const { testimonialsData } = Constants;
  const sourceData: TestimonialItem[] = (data ?? testimonialsData).map((item) => ({
    logo: item.logo,
    description: item.description,
    star: item.star,
    name: item.name,
    roleAtCompany: 'roleAtCompany' in item ? item.roleAtCompany : undefined,
    program: 'program' in item ? item.program : undefined,
    ctc: 'ctc' in item ? item.ctc : undefined,
    emoji: 'emoji' in item ? item.emoji : undefined,
  }));
  const total = sourceData?.length ?? 0;

  const [centerIdx, setCenterIdx] = useState(1);
  const [activeDotIdx, setActiveDotIdx] = useState(1);
  const [isSliding, setIsSliding] = useState(false);
  const [slideCommitted, setSlideCommitted] = useState(false);
  const [slidingForward, setSlidingForward] = useState(true);

  const isAnimatingRef = useRef(false);
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const themeConfig: Record<TestimonialThemeColor, { cardTheme: SectionTheme; indicatorColor: string; badgeClassName: string; badgeBorderClassName: string }> = {
    purple: { cardTheme: 'default', indicatorColor: colors.purple, badgeClassName: '', badgeBorderClassName: '' },
    green: { cardTheme: 'greentheme', indicatorColor: colors.portfolioGreen, badgeClassName: 'bg-black', badgeBorderClassName: 'bg-[linear-gradient(100.94deg,#8FE64A_0%,rgba(116,94,240,0.1)_50%,#8FE64A_100%)]' },
    rabbitx: { cardTheme: 'rabbitxtheme', indicatorColor: colors.rabbitXAccent, badgeClassName: 'bg-black', badgeBorderClassName: 'bg-[linear-gradient(100.94deg,#EE344C_0%,rgba(238,52,76,0.1)_50%,#EE344C_100%)]' },
    arbventure: { cardTheme: 'arbventuretheme', indicatorColor: colors.arbVentureThemeAccent, badgeClassName: 'bg-black', badgeBorderClassName: 'bg-[linear-gradient(90deg,#5F9937_0%,rgba(95,153,55,0)_50%,#5F9937_100%)]' },
    tatvacaretheme: {
      cardTheme: 'lightpinktheme',
      indicatorColor: colors.white,
      badgeClassName: '!bg-tatvacaretheme-badge-solid',
      badgeBorderClassName: 'bg-testimonial-border-tatvacaretheme-white-center',
    },
    ddxtheme: {
      cardTheme: 'ddxtheme',
      indicatorColor: colors.white,
      badgeClassName: '!bg-ddxtheme-badge-solid',
      badgeBorderClassName: 'bg-testimonial-border-ddxtheme-white-center',
    },
    medilearntheme: {
      cardTheme: 'medilearntheme',
      indicatorColor: colors.white,
      badgeClassName: '!bg-medilearntheme-badge-solid',
      badgeBorderClassName: 'bg-testimonial-border-medilearntheme-white-center',
    },
  };
  const selectedTheme = themeConfig[themeColor] ?? themeConfig.purple;
  const cardTheme: SectionTheme = selectedTheme.cardTheme;
  const resolvedIndicatorActiveColor = indicatorActiveColor ?? selectedTheme.indicatorColor;
  const resolvedIndicatorBorderColor = indicatorBorderColor ?? selectedTheme.indicatorColor;
  const resolvedTitleBadgeClassName = `${selectedTheme.badgeClassName} ${titleBadgeClassName}`.trim();
  const resolvedTitleBadgeBorderClassName = titleBadgeBorderClassName || selectedTheme.badgeBorderClassName;

  const mod = (n: number, m: number) => ((n % m) + m) % m;

  const stopAuto = useCallback(() => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const animateStep = useCallback((forward: boolean, onDone?: () => void) => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setSlidingForward(forward);
    setIsSliding(true);
    setSlideCommitted(false);

    // Dot updates immediately when slide starts
    setActiveDotIdx((prev) => forward ? mod(prev + 1, total) : mod(prev - 1, total));

    const prepId = window.setTimeout(() => {
      setSlideCommitted(true);
    }, SLIDE_PREP_MS);

    const commitId = window.setTimeout(() => {
      setCenterIdx((prev) => forward ? mod(prev + 1, total) : mod(prev - 1, total));
      setIsSliding(false);
      setSlideCommitted(false);
      isAnimatingRef.current = false;
      onDone?.();
    }, SLIDE_DURATION_MS);

    return () => { window.clearTimeout(prepId); window.clearTimeout(commitId); };
  }, [total]);

  const startAuto = useCallback(() => {
    stopAuto();
    if (!sourceData || total < VISIBLE_COUNT) return;
    autoTimerRef.current = setInterval(() => {
      if (!isAnimatingRef.current) animateStep(true);
    }, ROTATE_INTERVAL_MS);
  }, [animateStep, stopAuto, sourceData, total]);

  const animateSteps = (count: number, forward: boolean) => {
    if (count === 0 || isAnimatingRef.current) return;
    stopAuto();

    const doStep = (remaining: number) => {
      if (remaining === 0) { startAuto(); return; }
      animateStep(forward, () => doStep(remaining - 1));
    };
    doStep(count);
  };

  const handleDotClick = (dotIdx: number) => {
    if (isAnimatingRef.current || dotIdx === centerIdx) return;
    // Dot jumps to target immediately
    setActiveDotIdx(dotIdx);
    const stepsForward = mod(dotIdx - centerIdx, total);
    const stepsBackward = mod(centerIdx - dotIdx, total);
    if (stepsForward <= stepsBackward) {
      animateSteps(stepsForward, true);
    } else {
      animateSteps(stepsBackward, false);
    }
  };

  useEffect(() => {
    startAuto();
    return () => stopAuto();
  }, [startAuto, stopAuto]);

  const leftIdx     = mod(centerIdx - 1, total);
  const rightIdx    = mod(centerIdx + 1, total);
  const incomingIdx = slidingForward ? mod(centerIdx + 2, total) : mod(centerIdx - 2, total);

  const animatedTestimonials = (() => {
    if (!sourceData || total < VISIBLE_COUNT) return sourceData ?? [];
    const base = [
      sourceData[leftIdx],
      sourceData[centerIdx],
      sourceData[rightIdx],
    ];
    if (!isSliding) return base;
    return [...base, sourceData[incomingIdx]];
  })();

  const getSlot = (i: number): 'left' | 'center' | 'right' | 'incoming' | 'exit' => {
    if (!isSliding) {
      return i === 0 ? 'left' : i === 1 ? 'center' : 'right';
    }
    if (!slideCommitted) {
      return i === 0 ? 'left' : i === 1 ? 'center' : i === 2 ? 'right' : 'incoming';
    }
    if (slidingForward) {
      return i === 0 ? 'exit' : i === 1 ? 'left' : i === 2 ? 'center' : 'right';
    } else {
      return i === 0 ? 'left' : i === 1 ? 'center' : i === 2 ? 'right' : 'exit';
    }
  };

  const getSlideStyle = (slot: 'left' | 'center' | 'right' | 'incoming' | 'exit') => {
    switch (slot) {
      case 'left':     return { transform: 'translate(-165%, 7%) scale(0.94) rotate(-4deg)', opacity: 0.9, zIndex: 20 };
      case 'center':   return { transform: 'translate(-50%, -4%) scale(1.04) rotate(0deg)',   opacity: 1,   zIndex: 30 };
      case 'right':    return { transform: 'translate(65%, 7%) scale(0.94) rotate(4deg)',    opacity: 0.9, zIndex: 20 };
      case 'incoming': return { transform: 'translate(125%, 28%) scale(0.9) rotate(8deg)',    opacity: 0,   zIndex: 10 };
      case 'exit':
      default:         return { transform: 'translate(-225%, 28%) scale(0.9) rotate(-8deg)',  opacity: 0,   zIndex: 10 };
    }
  };

  const getDotStyle = (i: number) => {
    if (i === activeDotIdx) {
      return {
        backgroundColor: resolvedIndicatorActiveColor,
        borderColor: resolvedIndicatorActiveColor,
        transform: 'scale(1.15)',
      };
    }
    return {
      backgroundColor: 'transparent',
      borderColor: resolvedIndicatorBorderColor,
      opacity: themeColor === 'tatvacaretheme' || themeColor === 'ddxtheme' || themeColor === 'medilearntheme' ? 1 : 0.5,
    };
  };

  return (
    <section className={`w-full text-white ${className}`.trim()}>
      {showHeader && (
        <div className="mb-5 mt-6 flex items-center justify-center md:mb-5 md:mt-8 lg:mb-24">
          <TitleHeader
            badgeText="TESTIMONIALS"
            titleLines={['What customers say about us']}
            wrapperClassName="flex w-full max-w-4xl flex-col items-center text-center"
            titleClassName="text-4xl font-rajdhani font-semibold text-center md:text-4xl min-[1440px]:text-7xl"
            badgeClassName={`px-10 py-3 text-2xl md:px-12 md:py-4 text-2xl md:text-2xl min-[1440px]:px-14 min-[1440px]:text-[2rem] ${resolvedTitleBadgeClassName}`.trim()}
            badgeBorderClassName={resolvedTitleBadgeBorderClassName || undefined}
          />
        </div>
      )}

      <div className="flex justify-center px-4 min-[1440px]:px-0">
       <div className="relative h-[270px] w-full max-w-[1120px] overflow-hidden md:h-[380px] lg:h-[430px] xl:h-[520px] min-[1440px]:h-[58vw] min-[1440px]:max-w-[1700px] min-[1440px]:overflow-visible">
          {animatedTestimonials.map((item, i) => {
            const slot = getSlot(i);
            const side = slot === 'left' ? 'left' : slot === 'right' ? 'right' : undefined;
            const isCenter = slot === 'center';

            return (
              <div
                key={`${item.name}-${item.description}`}
                className="absolute left-1/2 top-0 transition-[transform,opacity] duration-700 ease-in will-change-transform"
                style={getSlideStyle(slot)}
              >
                <TestimonialCard
                  logo={item.logo}
                  description={item.description}
                  star={item.star}
                  name={item.name}
                  roleAtCompany={item.roleAtCompany}
                  program={item.program}
                  ctc={item.ctc}
                  emoji={item.emoji}
                  isCenter={isCenter}
                  side={side}
                  theme={cardTheme}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
        {sourceData?.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDotClick(i)}
            className="h-3.5 w-3.5 rounded-full border-2 transition-all duration-700 ease-in cursor-pointer"
            style={getDotStyle(i)}
            aria-label={`Go to testimonial ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Testimonial;
